import {
  addPendingOperation,
  cacheFile,
  cacheLesson,
  cacheLessons,
  getCachedFiles,
  getCachedLessons,
  getPendingOperations,
  removePendingOperation,
} from '../lib/indexedDb'
import { isCloudConfigured, supabase } from '../lib/supabase'
import type { Lesson, LessonFile, TranscriptionJob } from '../types'

export const LOCAL_USER_ID = '00000000-0000-0000-0000-000000000000'

const emptyLesson = (userId: string): Lesson => {
  const now = new Date().toISOString()
  return {
    id: crypto.randomUUID(),
    user_id: userId,
    subject: '',
    title: 'Жаңа сабақ',
    transcript: '',
    cleaned_transcript: '',
    summary: '',
    short_summary: '',
    key_points: [],
    terms: [],
    quiz: [],
    flashcards: [],
    created_at: now,
    updated_at: now,
    deleted_at: null,
  }
}

export const createLesson = async (userId: string) => {
  const lesson = emptyLesson(userId)
  await saveLesson(lesson)
  return lesson
}

export const listLessons = async () => {
  if (supabase) {
    const { data, error } = await supabase.from('lessons').select('*').order('updated_at', { ascending: false })
    if (!error && data) {
      const lessons = data as Lesson[]
      await cacheLessons(lessons)
      return lessons
    }
  }
  return (await getCachedLessons()).sort((a, b) => b.updated_at.localeCompare(a.updated_at))
}

export const saveLesson = async (lesson: Lesson) => {
  const updated = { ...lesson, updated_at: new Date().toISOString() }
  await cacheLesson(updated)
  if (!supabase) return updated

  const { error } = await supabase.from('lessons').upsert(updated)
  if (error) {
    await addPendingOperation({
      id: `lesson-${updated.id}`,
      type: 'save-lesson',
      payload: updated,
      createdAt: Date.now(),
    })
  }
  return updated
}

const safeFileName = (name: string) => {
  const extension = name.includes('.') ? `.${name.split('.').pop()!.toLowerCase()}` : ''
  const base = name
    .replace(/\.[^.]+$/, '')
    .normalize('NFKD')
    .replace(/[^\w-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'file'
  return `${base}${extension}`
}

export const uploadLessonFile = async (
  lessonId: string,
  userId: string,
  input: File | Blob,
  originalName: string,
  duration: number | null = null,
) => {
  const file = input instanceof File
    ? input
    : new File([input], originalName, { type: input.type || 'application/octet-stream' })
  const id = crypto.randomUUID()
  const path = `${userId}/${lessonId}/${id}-${safeFileName(file.name)}`
  const row: LessonFile = {
    id,
    lesson_id: lessonId,
    user_id: userId,
    file_name: file.name,
    storage_path: path,
    file_type: file.type || 'application/octet-stream',
    file_size: file.size,
    duration,
    created_at: new Date().toISOString(),
    deleted_at: null,
    local_blob: file,
  }
  await cacheFile(row)

  if (!supabase) return row

  const { error: uploadError } = await supabase.storage.from('lesson-files').upload(path, file, {
    contentType: row.file_type,
    upsert: false,
  })
  if (uploadError) {
    await addPendingOperation({
      id: `file-${id}`,
      type: 'upload-file',
      payload: { lessonId, userId, file },
      createdAt: Date.now(),
    })
    throw new Error(`Файл бұлтқа жүктелмеді: ${uploadError.message}`)
  }

  const { error: rowError } = await supabase.from('lesson_files').insert({
    ...row,
    local_blob: undefined,
  })
  if (rowError) throw new Error(`Файл жазбасы сақталмады: ${rowError.message}`)
  return row
}

export const listLessonFiles = async (lessonId: string) => {
  const cached = await getCachedFiles(lessonId)
  if (!supabase) return cached.filter((file) => !file.deleted_at)

  const { data, error } = await supabase
    .from('lesson_files')
    .select('*')
    .eq('lesson_id', lessonId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
  if (error || !data) return cached.filter((file) => !file.deleted_at)
  await Promise.all((data as LessonFile[]).map(cacheFile))
  return data as LessonFile[]
}

export const getFileBlob = async (file: LessonFile) => {
  if (file.local_blob) return file.local_blob
  if (!supabase) throw new Error('Файлдың жергілікті көшірмесі табылмады.')
  const { data, error } = await supabase.storage.from('lesson-files').download(file.storage_path)
  if (error || !data) throw new Error(`Файл жүктелмеді: ${error?.message ?? 'белгісіз қате'}`)
  return data
}

export const softDeleteFile = async (file: LessonFile) => {
  const deleted = { ...file, deleted_at: new Date().toISOString() }
  await cacheFile(deleted)
  if (supabase) {
    const { error } = await supabase.from('lesson_files').update({ deleted_at: deleted.deleted_at }).eq('id', file.id)
    if (error) throw new Error(error.message)
  }
}

export const createTranscriptionJob = async (
  lessonId: string,
  fileId: string,
  userId: string,
  language: string,
) => {
  const now = new Date().toISOString()
  const job: TranscriptionJob = {
    id: crypto.randomUUID(),
    lesson_id: lessonId,
    file_id: fileId,
    user_id: userId,
    status: 'queued',
    progress: 0,
    language,
    error_message: null,
    created_at: now,
    updated_at: now,
  }
  if (supabase) await supabase.from('transcription_jobs').insert(job)
  return job
}

export const updateTranscriptionJob = async (
  id: string,
  values: Partial<Pick<TranscriptionJob, 'status' | 'progress' | 'error_message'>>,
) => {
  if (!supabase) return
  await supabase
    .from('transcription_jobs')
    .update({ ...values, updated_at: new Date().toISOString() })
    .eq('id', id)
}

export const syncPendingOperations = async () => {
  if (!isCloudConfigured || !supabase || !navigator.onLine) return 0
  const operations = await getPendingOperations()
  let completed = 0
  for (const operation of operations) {
    if (operation.type === 'save-lesson') {
      const { error } = await supabase.from('lessons').upsert(operation.payload as Lesson)
      if (error) continue
      await removePendingOperation(operation.id)
      completed += 1
    }
    // File retry needs its original lesson context and uses the normal upload pipeline.
    if (operation.type === 'upload-file') {
      const payload = operation.payload as { lessonId: string; userId: string; file: File }
      try {
        await uploadLessonFile(payload.lessonId, payload.userId, payload.file, payload.file.name)
        await removePendingOperation(operation.id)
        completed += 1
      } catch {
        // Интернет қайта тұрақталған кезде келесі рет тағы орындалады.
      }
    }
  }
  return completed
}
